import React, { useMemo, useState } from 'react';
import { ClockCircleOutlined } from '@ant-design/icons';
import { useRequest } from 'ahooks';
import dayjs from 'dayjs';
import { Spin } from 'antd';
import styles from './index.module.scss';
import titleIcon from '@/assets/images/title_icon.png';
import strategyIcon from '../../imgs/icon-strategy.png';
import safetyIcon from '../../imgs/icon-safety.png';
import circleIcon from '../../imgs/icon-circle.png';
import { DatePicker } from '@maxtropy/components';
import {
  apiV2AirCompressorOverviewGetStrategyListPost,
  V2AirCompressorOverviewGetStrategyListPostResponse,
} from '@maxtropy/device-customer-apis-v2';
import { OVERVIEW_POLLING_INTERVAL } from '../../utils';

const formatValue = (value?: number, unit = '') => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '--';
  return `${value}${unit}`;
};

const formatLoadRate = (value?: number) => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '--';
  const percent = value > 1 ? value : value * 100;
  return `${percent.toFixed(2)}%`;
};

const formatTime = (value?: number) => {
  if (!value) return '--';
  return dayjs(value).format('YYYY年M月D日 HH:mm:ss');
};

const DemandActionContent: React.FC<{
  suggestClose?: string[];
  suggestOpen?: string[];
}> = ({ suggestClose, suggestOpen }) => {
  if (!suggestClose?.length && !suggestOpen?.length) {
    return <span>--</span>;
  }

  const parts: React.ReactNode[] = [];

  if (suggestClose?.length) {
    parts.push(
      <span key="close">
        关闭 <span style={{ color: '#FF4D4F' }}>{suggestClose.join('、')}</span>
      </span>
    );
  }

  if (suggestOpen?.length) {
    parts.push(
      <span key="open">
        开启 <span style={{ color: '#16DD8E' }}>{suggestOpen.join('、')}</span>
      </span>
    );
  }

  return (
    <>
      {parts.map((part, index) => (
        <React.Fragment key={index}>
          {index > 0 && '，'}
          {part}
        </React.Fragment>
      ))}
    </>
  );
};

const mapPressureContent = (
  item: NonNullable<V2AirCompressorOverviewGetStrategyListPostResponse['supplyPressureGuaranteeStrategies']>[number]
) => {
  const minPressure = typeof item.needPressureMin === 'number' ? `${item.needPressureMin}Mpa` : '--';
  const maxPressure = typeof item.needPressureMax === 'number' ? `${item.needPressureMax}Mpa` : '--';
  const suggestOpenText =
    item.suggestOpen
      ?.map(suggest => `${suggest.station || '--'} ${suggest.airCompressors?.join('、') || ''}`)
      .filter(Boolean)
      .join('、') ?? '';

  return (
    <>
      不满足供气条件{minPressure}~{maxPressure}，需要开启
      <span style={{ color: '#16DD8E' }}>{suggestOpenText || '--'}</span>
      ，保障供气压力。
    </>
  );
};

const StrategyPanel: React.FC = () => {
  const [selectedDate, setSelectedDate] = useState(dayjs());
  const disabledDate = (current: dayjs.Dayjs) => {
    return current.isAfter(dayjs().endOf('day'));
  };

  const { data, loading } = useRequest(
    () =>
      apiV2AirCompressorOverviewGetStrategyListPost({
        time: selectedDate.startOf('day').valueOf(),
      }),
    {
      pollingInterval: OVERVIEW_POLLING_INTERVAL,
      refreshDeps: [selectedDate.valueOf()],
    }
  );

  const demandStrategies = useMemo(() => data?.demandControlStrategies ?? [], [data?.demandControlStrategies]);
  const pressureStrategies = useMemo(
    () => data?.supplyPressureGuaranteeStrategies ?? [],
    [data?.supplyPressureGuaranteeStrategies]
  );
  const renderEmptyState = (text: string) => (
    <div className={styles.emptyState}>
      <div className={styles.emptyIconWrap}>
        <img src={circleIcon} alt="" className={styles.emptyCircleIcon} />
        <img src={safetyIcon} alt="" className={styles.emptySafetyIcon} />
      </div>
      <p className={styles.emptyText}>{text}</p>
    </div>
  );

  return (
    <section className={styles.panel}>
      <div className={styles.headerRow}>
        <div className={styles.mainTitle}>空压机策略</div>
        <DatePicker
          style={{ width: 200 }}
          value={selectedDate}
          defaultValue={dayjs()}
          disabledDate={disabledDate}
          onChange={value => setSelectedDate(value || dayjs())}
          getPopupContainer={() => document.querySelector('.compressorOverviewPage') || document.body}
        />
      </div>

      <Spin spinning={loading && !data} wrapperClassName={styles.panelContentLoading}>
        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <img src={titleIcon} alt="" className={styles.sectionTitleIcon} />
            <span>需量控制策略</span>
          </div>
          <div className={styles.cardList}>
            {demandStrategies.length
              ? demandStrategies.map((item, index) => {
                  const changeList = [...(item.currentChange ?? []), ...(item.otherChange ?? [])];
                  const executeContent = changeList.length
                    ? changeList.map((change, changeIndex) => {
                        const changeDirection = change.changeType === 1 ? '上升至' : '降低至';
                        const changeLabel = change.changeType === 1 ? '上升' : '降低';
                        return (
                          <React.Fragment key={`${change.circuit || 'change'}-${changeIndex}`}>
                            {changeIndex > 0 && '，'}
                            {change.circuit || '--'}功率{changeDirection}
                            <span className={styles.executePowerValue}>{formatValue(change.targetPower, 'kW')}</span>
                            （控制阈值：
                            <span className={styles.executeThresholdValue}>
                              {formatValue(change.demandThreshold, 'kW')}
                            </span>
                            ），功率{changeLabel}后，负载率为
                            <span className={styles.executeLoadRateValue}>{formatLoadRate(change.loadRateAfter)}</span>
                          </React.Fragment>
                        );
                      })
                    : '--';
                  return (
                    <section key={`${item.circuit || 'demand'}-${item.time || index}`} className={styles.card}>
                      <div className={styles.cardHeader}>
                        <div className={styles.cardHeaderLeft}>
                          <img src={strategyIcon} alt="" className={styles.cardTitleIcon} />
                          <span className={styles.cardTitle}>{item.circuit || '--'}</span>
                          <span className={styles.cardMetaLabel}>当前功率:</span>
                          <span className={styles.cardMetaValue}>{formatValue(item.currentPower, 'kW')}</span>
                          <span className={styles.cardMetaLabel}>控制阈值:</span>
                          <span className={styles.cardMetaWarn}>{formatValue(item.demandThreshold, 'kW')}</span>
                        </div>
                        <div className={styles.cardTime}>
                          <ClockCircleOutlined />
                          <span>{formatTime(item.time)}</span>
                        </div>
                      </div>

                      <div className={styles.cardBody}>
                        <div className={styles.actionRow}>
                          <div className={styles.actionTag}>建议</div>
                          <div className={styles.actionContent}>
                            <DemandActionContent suggestClose={item.suggestClose} suggestOpen={item.suggestOpen} />
                          </div>
                        </div>
                        <div className={styles.actionRow}>
                          <div className={styles.actionTag}>执行后</div>
                          <div className={styles.actionContent}>{executeContent}</div>
                        </div>
                      </div>
                    </section>
                  );
                })
              : renderEmptyState(
                  '基于实时监测数据，系统判断当日无需启动需量优化策略，建议保持现有运行模式，持续关注功率变化趋势。'
                )}
          </div>
        </div>

        <div className={styles.section}>
          <div className={styles.sectionTitle}>
            <img src={titleIcon} alt="" className={styles.sectionTitleIcon} />
            <span>供气压力保障策略</span>
          </div>
          <div className={styles.cardList}>
            {pressureStrategies.length
              ? pressureStrategies.map((item, index) => (
                  <section key={`${item.station || 'pressure'}-${index}`} className={styles.card}>
                    <div className={styles.cardHeader}>
                      <div className={styles.cardHeaderLeft}>
                        <img src={strategyIcon} alt="" className={styles.cardTitleIcon} />
                        <span className={styles.cardTitle}>{item.station || '--'}</span>
                        <span className={styles.cardMetaLabel}>当前总管压力:</span>
                        <span className={styles.cardMetaValue}>{formatValue(item.pressure, 'Mpa')}</span>
                      </div>
                      <div className={styles.cardTime}>
                        <ClockCircleOutlined />
                        <span>{formatTime(item.time)}</span>
                      </div>
                    </div>
                    <div className={styles.cardBodySingle}>{mapPressureContent(item)}</div>
                  </section>
                ))
              : renderEmptyState(
                  '当日各监测点位压力持续正常，系统适合评估运行阀值与日元策略调整气供需，建议维持当前供气参数，确保持续稳定满足生产用气需求。'
                )}
          </div>
        </div>
      </Spin>
    </section>
  );
};

export default StrategyPanel;
